import {ArrowRight, Check, X} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {formatUsd} from '@client/pages/public/enroll/enrollModel';
import {useLinkHref} from '@client/pages/public/enroll/enrollPreview';
import type {CoverageTier, PlanConfig} from '@client/pages/public/enroll/types';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';

interface PlanCardProps {
  plan: PlanConfig;
  tier: CoverageTier;
  selected: boolean;
  onSelect: () => void;
  badge?: string;
  accentColor?: string;
  continueLabel?: string;
  onContinue?: () => void;
  /** When set, the selected state becomes a toggle that clears the choice (opt-in add-ons only). */
  onDeselect?: () => void;
}

export function PlanCard({plan, tier, selected, onSelect, badge, accentColor, continueLabel, onContinue, onDeselect}: PlanCardProps) {
  const price = plan.rates[tier];
  const payPeriod = sitePayPeriod(useEnrollGroup());
  const linkHref = useLinkHref();

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-xl border bg-white p-5 transition-shadow',
        selected ? 'shadow-md ring-2' : 'border-neutral-200 hover:shadow-sm',
        POSTHOG_UNMASK_CLASS,
      )}
      style={
        selected
          ? ({borderColor: 'var(--enroll-accent)', '--tw-ring-color': 'var(--enroll-accent)'} as React.CSSProperties)
          : undefined
      }
    >
      {badge && (
        <span
          className='absolute -top-2 left-5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white'
          style={{backgroundColor: 'var(--enroll-accent)'}}
        >
          {badge}
        </span>
      )}

      <div className='flex items-center gap-2'>
        {accentColor && (
          <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{backgroundColor: accentColor}} />
        )}
        <h3 className='text-lg font-semibold text-neutral-900'>{plan.name}</h3>
      </div>
      <p className='mt-1 min-h-10 text-sm text-neutral-500'>{plan.tagline}</p>

      <div className='mt-4'>
        <div className='text-3xl font-bold text-[var(--enroll-ink)]'>{formatUsd(price)}</div>
        <div className='text-sm text-neutral-500'>{payPeriod.each}</div>
      </div>

      {plan.highlights.length > 0 && (
        <ul data-equalize='highlights' className='mt-4 space-y-2'>
          {plan.highlights.map((highlight) => (
            <li key={highlight} className='flex items-start gap-2 text-sm text-neutral-700'>
              <Check className='mt-0.5 h-4 w-4 shrink-0 text-green-600' />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      )}

      {plan.limitedBenefit && (
        <p className='mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700'>
          Not ACA / not minimum essential coverage (MEC).
        </p>
      )}

      <div className='mt-auto pt-5'>
        {selected ? (
          <div className='space-y-2'>
            {onDeselect ? (
              <button
                type='button'
                onClick={onDeselect}
                aria-label={`Remove ${plan.name}`}
                className='group inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold transition-colors'
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--enroll-accent) 12%, white)',
                  borderColor: 'var(--enroll-accent)',
                  color: 'var(--enroll-accent)',
                }}
              >
                <span className='inline-flex items-center gap-2 group-hover:hidden'>
                  <Check className='h-4 w-4' />
                  Selected
                </span>
                <span className='hidden items-center gap-2 group-hover:inline-flex'>
                  <X className='h-4 w-4' />
                  Remove
                </span>
              </button>
            ) : (
              <div
                className='inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold'
                style={{
                  backgroundColor: 'color-mix(in srgb, var(--enroll-accent) 12%, white)',
                  borderColor: 'var(--enroll-accent)',
                  color: 'var(--enroll-accent)',
                }}
              >
                <Check className='h-4 w-4' />
                Selected
              </div>
            )}
            {onContinue && (
              <button
                type='button'
                onClick={onContinue}
                className='inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
              >
                {continueLabel ? `Continue to ${continueLabel}` : 'Continue'}
                <ArrowRight className='h-4 w-4' />
              </button>
            )}
          </div>
        ) : (
          <div className='space-y-2'>
            {onContinue && <div aria-hidden className='h-11' />}
            <button
              type='button'
              onClick={onSelect}
              className='inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-5 text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-50'
            >
              Choose plan
            </button>
          </div>
        )}

        {plan.sbcUrl && (
          <a
            href={linkHref(plan.sbcUrl)}
            target='_blank'
            rel='noreferrer'
            className='mt-3 inline-flex w-full items-center justify-center text-xs font-medium text-neutral-500 underline-offset-4 transition-colors hover:text-[var(--enroll-ink)] hover:underline'
          >
            View Summary of Benefits (PDF)
          </a>
        )}
      </div>
    </div>
  );
}
